import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  MessageSquare,
  BarChart,
  MessageCircle,
  ExternalLink,
  Download,
  Users,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'react-toastify';
import { chatbotAdminApi } from '../../api/chatbotApi';
import { ChatbotInstance, ChatbotMetrics } from '../../types/chatbot.types';
import { useAppSelector } from '@/store/hooks';
import { selectCurrentUser } from '@/features/auth/authSlice';
import { MetricsDashboard } from '../../components/admin/MetricsDashboard';
import { ConversationViewer } from '../../components/admin/ConversationViewer';
import { useConfirm } from '@/contexts/confirmContext';

type SummaryCard = {
  label: string;
  value: number;
  icon: typeof MessageSquare;
};

function getSummaryNumber(data: unknown, key: string): number {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return 0;
  const value = (data as Record<string, unknown>)[key];
  return typeof value === 'number' ? value : 0;
}

export function ChatbotInstancesPage() {
  const navigate = useNavigate();
  const currentUser = useAppSelector(selectCurrentUser);
  const isSuperAdmin = currentUser?.role === 'SUPERADMIN';
  const hasAnalytics = isSuperAdmin || currentUser?.tenantFeatures?.includes('analytics');
  const hasExport = isSuperAdmin || currentUser?.tenantFeatures?.includes('export');
  const { confirm } = useConfirm();

  const [instances, setInstances] = useState<ChatbotInstance[]>([]);
  const [filteredInstances, setFilteredInstances] = useState<ChatbotInstance[]>([]);
  const [loading, setLoading] = useState(true);
  const [tenantFilter, setTenantFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [accessTypeFilter, setAccessTypeFilter] = useState<string>('ALL');

  const [showMetrics, setShowMetrics] = useState(false);
  const [metrics, setMetrics] = useState<ChatbotMetrics | null>(null);
  const [metricsTitle, setMetricsTitle] = useState('Métricas de Instancia');
  const [summaryData, setSummaryData] = useState<unknown>(null);
  const [showSummary, setShowSummary] = useState(false);
  const [topUsers, setTopUsers] = useState<
    Array<{
      userId: string;
      identifier: string;
      messageCount: number;
      conversationCount: number;
    }>
  >([]);
  const [showTopUsers, setShowTopUsers] = useState(false);
  const [showConversations, setShowConversations] = useState(false);
  const [selectedInstanceId, setSelectedInstanceId] = useState<string | null>(null);

  useEffect(() => {
    loadInstances();
  }, [tenantFilter]);

  useEffect(() => {
    filterInstances();
  }, [instances, statusFilter, accessTypeFilter]);

  const loadInstances = async () => {
    try {
      setLoading(true);
      const tenantIdParam = isSuperAdmin
        ? tenantFilter || undefined
        : currentUser?.tenantId || undefined;
      const data = await chatbotAdminApi.listInstances(tenantIdParam);
      setInstances(data);
    } catch (error) {
      toast.error('Error al cargar instancias');
    } finally {
      setLoading(false);
    }
  };

  const filterInstances = () => {
    let filtered = [...instances];

    if (statusFilter !== 'ALL') {
      filtered = filtered.filter(i => i.status === statusFilter);
    }

    if (accessTypeFilter !== 'ALL') {
      filtered = filtered.filter(i => i.accessType === accessTypeFilter);
    }

    setFilteredInstances(filtered);
  };

  const handleDelete = async (id: string) => {
    try {
      const confirmed = await confirm({
        title: '¿Está seguro?',
        message:
          'Esta acción eliminará permanentemente esta instancia de chatbot. Esta acción no se puede deshacer.',
        confirmText: 'Eliminar',
        cancelText: 'Cancelar',
      });

      if (!confirmed) return;

      await chatbotAdminApi.deleteInstance(id);
      toast.success('Instancia eliminada');
      loadInstances();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Error al eliminar instancia');
    }
  };

  const handleMetrics = async (id: string) => {
    try {
      const data = await chatbotAdminApi.getMetrics(id);
      setMetrics(data);
      setMetricsTitle('Métricas de Instancia');
      setShowMetrics(true);
    } catch (error) {
      toast.error('Error al cargar métricas');
    }
  };

  const handleTenantMetrics = async () => {
    const tenantId = isSuperAdmin ? tenantFilter : currentUser?.tenantId;
    if (!tenantId) {
      toast.error('Seleccioná un Tenant ID para ver métricas del tenant');
      return;
    }
    try {
      const data = await chatbotAdminApi.getTenantMetrics(tenantId);
      setSummaryData(data);
      setMetricsTitle('Métricas por Tenant');
      setShowSummary(true);
    } catch (error) {
      toast.error('Error al cargar métricas del tenant');
    }
  };

  const handleUsageSummary = async () => {
    try {
      const data = await chatbotAdminApi.getUsageSummary(
        isSuperAdmin ? tenantFilter || undefined : currentUser?.tenantId || undefined
      );
      setSummaryData(data);
      setMetricsTitle('Resumen de Uso');
      setShowSummary(true);
    } catch (error) {
      toast.error('Error al cargar resumen de uso');
    }
  };

  const downloadBlob = (blob: Blob, fileName: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
  };

  const downloadJson = (data: unknown, fileName: string) => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    downloadBlob(blob, fileName);
  };

  const handleExportMetrics = async () => {
    try {
      const data = await chatbotAdminApi.exportMetrics(
        isSuperAdmin ? tenantFilter || undefined : currentUser?.tenantId || undefined
      );
      downloadJson(data, 'chatbot-metrics-export.json');
    } catch (error) {
      toast.error('Error al exportar métricas');
    }
  };

  const handleExportConversations = async (id: string, format: 'JSON' | 'CSV' | 'PDF') => {
    try {
      const blob = await chatbotAdminApi.exportConversations(id, format);
      downloadBlob(blob, `conversations-export.${format.toLowerCase()}`);
    } catch (error) {
      toast.error('Error al exportar conversaciones');
    }
  };

  const handleTopUsers = async (id: string) => {
    try {
      setSelectedInstanceId(id);
      setTopUsers(await chatbotAdminApi.getTopUsers(id));
      setShowTopUsers(true);
    } catch (error) {
      toast.error('Error al cargar top users');
    }
  };

  const handleConversations = (id: string) => {
    setSelectedInstanceId(id);
    setShowConversations(true);
  };

  const getStatusBadge = (status: string) => {
    return status === 'ACTIVE' ? (
      <Badge className='bg-green-500'>Activo</Badge>
    ) : (
      <Badge variant='secondary'>Inactivo</Badge>
    );
  };

  const getAccessTypeBadge = (accessType: string) => {
    const labels: Record<string, string> = {
      ANONYMOUS: 'Anónimo',
      WHITELIST: 'Whitelist',
      DOMAIN: 'Dominio',
      OPEN: 'Abierto',
    };
    return <Badge variant='outline'>{labels[accessType] || accessType}</Badge>;
  };

  const getSummaryCards = (): SummaryCard[] => [
    {
      label: 'Total Instancias',
      value: getSummaryNumber(summaryData, 'totalInstances'),
      icon: MessageSquare,
    },
    {
      label: 'Instancias Activas',
      value: getSummaryNumber(summaryData, 'activeInstances'),
      icon: BarChart,
    },
    {
      label: 'Total Usuarios',
      value: getSummaryNumber(summaryData, 'totalUsers'),
      icon: Users,
    },
    {
      label: 'Total Conversaciones',
      value: getSummaryNumber(summaryData, 'totalConversations'),
      icon: MessageCircle,
    },
    {
      label: 'Total Mensajes',
      value: getSummaryNumber(summaryData, 'totalMessages'),
      icon: MessageSquare,
    },
    {
      label: 'Mensajes 24h',
      value: getSummaryNumber(summaryData, 'last24hMessages'),
      icon: BarChart,
    },
  ];

  const renderSummaryContent = () => (
    <div className='grid gap-4 md:grid-cols-2 lg:grid-cols-3'>
      {getSummaryCards().map(card => {
        const Icon = card.icon;
        return (
          <Card key={card.label}>
            <CardContent className='pt-6'>
              <div className='flex items-center justify-between'>
                <div>
                  <p className='text-sm font-medium text-muted-foreground'>{card.label}</p>
                  <p className='text-2xl font-bold'>{card.value}</p>
                </div>
                <Icon className='h-4 w-4 text-muted-foreground' />
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );

  const renderInstancesContent = () => {
    if (loading) {
      return (
        <div className='flex justify-center py-8'>
          <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary'></div>
        </div>
      );
    }

    if (filteredInstances.length === 0) {
      return (
        <div className='text-center py-8 text-muted-foreground'>No hay instancias configuradas</div>
      );
    }

    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Nombre</TableHead>
            <TableHead>Tenant</TableHead>
            <TableHead>Estado</TableHead>
            <TableHead>Tipo de Acceso</TableHead>
            <TableHead>Bots</TableHead>
            <TableHead className='text-right'>Acciones</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filteredInstances.map(instance => (
            <TableRow key={instance.id}>
              <TableCell>
                <div>
                  <div className='font-medium'>{instance.name}</div>
                  {instance.description && (
                    <div className='text-sm text-muted-foreground'>{instance.description}</div>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <span className='text-sm font-medium'>{instance.tenantName || 'N/A'}</span>
              </TableCell>
              <TableCell>{getStatusBadge(instance.status)}</TableCell>
              <TableCell>{getAccessTypeBadge(instance.accessType)}</TableCell>
              <TableCell>{instance.botsCount || 0}</TableCell>
              <TableCell className='text-right'>
                <div className='flex justify-end gap-2'>
                  <Button
                    variant='ghost'
                    size='icon'
                    title='Abrir Chat'
                    onClick={() => window.open(`/chatbot/${instance.id}`, '_blank')}
                  >
                    <ExternalLink className='h-4 w-4' />
                  </Button>
                  {hasAnalytics && (
                    <>
                      <Button
                        variant='ghost'
                        size='icon'
                        title='Métricas'
                        onClick={() => handleMetrics(instance.id)}
                      >
                        <BarChart className='h-4 w-4' />
                      </Button>
                      <Button
                        variant='ghost'
                        size='icon'
                        title='Top users'
                        onClick={() => handleTopUsers(instance.id)}
                      >
                        <Users className='h-4 w-4' />
                      </Button>
                    </>
                  )}
                  <Button
                    variant='ghost'
                    size='icon'
                    title='Conversaciones'
                    onClick={() => handleConversations(instance.id)}
                  >
                    <MessageCircle className='h-4 w-4' />
                  </Button>
                  {hasExport && (
                    <select
                      title='Exportar conversaciones'
                      className='border border-input bg-background text-foreground rounded-md px-2 text-xs'
                      defaultValue=''
                      onChange={event => {
                        const format = event.target.value as 'JSON' | 'CSV' | 'PDF';
                        event.target.value = '';
                        if (format) handleExportConversations(instance.id, format);
                      }}
                    >
                      <option value='' disabled>
                        Exportar
                      </option>
                      <option value='CSV'>CSV</option>
                      <option value='JSON'>JSON</option>
                      <option value='PDF'>PDF</option>
                    </select>
                  )}
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={() => navigate(`/admin/chatbot/${instance.id}`)}
                  >
                    Editar
                  </Button>
                  <Button variant='destructive' size='sm' onClick={() => handleDelete(instance.id)}>
                    Eliminar
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  return (
    <div className='container mx-auto py-6'>
      <Card>
        <CardHeader className='flex flex-row items-center justify-between'>
          <div className='flex items-center gap-2'>
            <MessageSquare className='h-6 w-6' />
            <CardTitle>Instancias de Chatbot</CardTitle>
          </div>
          <div className='flex gap-2'>
            <Button variant='outline' onClick={() => navigate('/admin/chatbot/bots')}>
              Bots IA
            </Button>
            <Button onClick={() => navigate('/admin/chatbot/new')}>
              <Plus className='h-4 w-4 mr-2' />
              Nueva Instancia
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className='flex flex-wrap gap-2 mb-4'>
            {hasAnalytics && (
              <>
                <Button variant='outline' size='sm' onClick={handleTenantMetrics}>
                  <BarChart className='h-4 w-4 mr-2' />
                  Métricas Tenant
                </Button>
                <Button variant='outline' size='sm' onClick={handleUsageSummary}>
                  <MessageSquare className='h-4 w-4 mr-2' />
                  Resumen Uso
                </Button>
              </>
            )}
            {hasExport && (
              <Button variant='outline' size='sm' onClick={handleExportMetrics}>
                <Download className='h-4 w-4 mr-2' />
                Exportar Métricas
              </Button>
            )}
          </div>
          <div className='flex flex-wrap gap-4 mb-4'>
            {isSuperAdmin && (
              <Input
                placeholder='Filtrar por Tenant ID...'
                value={tenantFilter}
                onChange={e => setTenantFilter(e.target.value)}
                className='max-w-xs'
              />
            )}

            <select
              className='border border-input bg-background text-foreground rounded-md p-2'
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
            >
              <option value='ALL'>Todos los estados</option>
              <option value='ACTIVE'>Activo</option>
              <option value='INACTIVE'>Inactivo</option>
            </select>

            <select
              className='border border-input bg-background text-foreground rounded-md p-2'
              value={accessTypeFilter}
              onChange={e => setAccessTypeFilter(e.target.value)}
            >
              <option value='ALL'>Todos los accesos</option>
              <option value='ANONYMOUS'>Anónimo</option>
              <option value='WHITELIST'>Whitelist</option>
              <option value='DOMAIN'>Dominio</option>
              <option value='OPEN'>Abierto</option>
            </select>
          </div>

          {renderInstancesContent()}
        </CardContent>
      </Card>

      {/* Metrics Modal */}
      <Dialog open={showMetrics} onOpenChange={setShowMetrics}>
        <DialogContent className='max-w-4xl'>
          <DialogHeader>
            <DialogTitle>{metricsTitle}</DialogTitle>
          </DialogHeader>
          {metrics && <MetricsDashboard metrics={metrics} />}
        </DialogContent>
      </Dialog>

      <Dialog open={showSummary} onOpenChange={setShowSummary}>
        <DialogContent className='max-w-4xl'>
          <DialogHeader>
            <DialogTitle>{metricsTitle}</DialogTitle>
          </DialogHeader>
          {renderSummaryContent()}
        </DialogContent>
      </Dialog>

      <Dialog open={showTopUsers} onOpenChange={setShowTopUsers}>
        <DialogContent className='max-w-3xl'>
          <DialogHeader>
            <DialogTitle>Top Users</DialogTitle>
          </DialogHeader>
          <div className='border rounded-lg overflow-hidden'>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Usuario</TableHead>
                  <TableHead>Mensajes</TableHead>
                  <TableHead>Conversaciones</TableHead>
                  {(hasAnalytics || hasExport) && (
                    <TableHead className='text-right'>Acciones</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {topUsers.map(user => (
                  <TableRow key={user.userId}>
                    <TableCell>{user.identifier}</TableCell>
                    <TableCell>{user.messageCount}</TableCell>
                    <TableCell>{user.conversationCount}</TableCell>
                    {(hasAnalytics || hasExport) && (
                      <TableCell className='text-right'>
                        <div className='flex justify-end gap-2'>
                          {hasAnalytics && (
                            <Button
                              variant='outline'
                              size='sm'
                              onClick={async () => {
                                if (!selectedInstanceId) return;
                                try {
                                  setSummaryData(
                                    await chatbotAdminApi.getUserMetrics(
                                      selectedInstanceId,
                                      user.userId
                                    )
                                  );
                                  setMetricsTitle(`Métricas de ${user.identifier}`);
                                  setShowSummary(true);
                                } catch {
                                  toast.error('Error al cargar métricas del usuario');
                                }
                              }}
                            >
                              Ver métricas
                            </Button>
                          )}
                          {hasExport && (
                            <select
                              className='border border-input bg-background text-foreground rounded-md px-2 text-xs'
                              defaultValue=''
                              onChange={async event => {
                                const format = event.target.value as 'JSON' | 'CSV' | 'PDF';
                                event.target.value = '';
                                if (!selectedInstanceId || !format) return;
                                try {
                                  const blob = await chatbotAdminApi.exportUserConversations(
                                    selectedInstanceId,
                                    user.userId,
                                    format
                                  );
                                  downloadBlob(
                                    blob,
                                    `user-conversations-${user.userId}.${format.toLowerCase()}`
                                  );
                                } catch {
                                  toast.error('Error al exportar conversaciones del usuario');
                                }
                              }}
                            >
                              <option value='' disabled>
                                Exportar
                              </option>
                              <option value='CSV'>CSV</option>
                              <option value='JSON'>JSON</option>
                              <option value='PDF'>PDF</option>
                            </select>
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>

      {/* Conversation Viewer Modal */}
      {selectedInstanceId && (
        <ConversationViewer
          instanceId={selectedInstanceId}
          open={showConversations}
          onOpenChange={setShowConversations}
          canExport={hasExport}
        />
      )}
    </div>
  );
}

export default ChatbotInstancesPage;
